import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  confirmSignUp,
  resendSignUpCode,
  fetchAuthSession,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  setAuthLoading,
  setAuthUser,
  authLoginSuccess,
  authLoginFailure,
  authLogout,
} from '../redux/actions/authActions';
import userService from '../services/UserService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const {
    user,
    loading,
    error,
    isAuthenticated,
  } = useSelector(state => state.auth);
  const [localError, setLocalError] = useState(null);

  const fetchUserData = useCallback(async () => {
    try {
      return await userService.getCurrentUser();
    } catch (fetchError) {
      console.error('Failed to fetch user data:', fetchError);
      return null;
    }
  }, []);

  const checkAuthState = useCallback(async () => {
    try {
      dispatch(setAuthLoading(true));
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      // Fetch user data from backend
      const userData = token ? await fetchUserData() : null;
      
      // Combine Cognito user with backend user data
      const enhancedUser = {
        ...currentUser,
        userData,
      };
      
      dispatch(authLoginSuccess(enhancedUser, token));
    } catch (authError) {
      dispatch(setAuthUser(null));
    } finally {
      dispatch(setAuthLoading(false));
    }
  }, [dispatch, fetchUserData]);

  useEffect(() => {
    checkAuthState();

    // Listen for OAuth events
    const hubUnsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      
      if (event === 'signInWithRedirect') {
        // OAuth sign-in completed successfully
        checkAuthState();
      } else if (event === 'signInWithRedirect_failure') {
        // OAuth sign-in failed
        console.error('OAuth sign-in failed:', payload.data);
        dispatch(authLoginFailure('OAuth sign-in failed'));
        dispatch(setAuthLoading(false));
      }
    });

    return () => {
      hubUnsubscribe();
    };
  }, [checkAuthState, dispatch]);

  const login = async (email, password) => {
    try {
      setLocalError(null);
      dispatch(setAuthLoading(true));
      const { isSignedIn } = await signIn({
        username: email,
        password,
      });

      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        // Fetch user data from backend
        const userData = token ? await fetchUserData() : null;
        
        // Combine Cognito user with backend user data
        const enhancedUser = {
          ...currentUser,
          userData,
        };

        dispatch(authLoginSuccess(enhancedUser, token));
        return { success: true };
      }
      return { success: false, error: 'Sign in failed' };
    } catch (authError) {
      dispatch(authLoginFailure(authError.message));
      return { success: false, error: authError.message };
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

  const signup = async (email, password, fullName) => {
    try {
      setLocalError(null);
      dispatch(setAuthLoading(true));

      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name: fullName,
          },
        },
      });

      return {
        success: true,
        isSignUpComplete,
        userId,
        nextStep,
        username: email, // Return email as username for confirmation
        requiresConfirmation: nextStep?.signUpStep === 'CONFIRM_SIGN_UP',
      };
    } catch (authError) {
      dispatch(authLoginFailure(authError.message));
      return { success: false, error: authError.message };
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

  const confirmSignup = async (username, confirmationCode) => {
    try {
      setLocalError(null);
      dispatch(setAuthLoading(true));
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username,
        confirmationCode,
      });

      if (isSignUpComplete) {
        return { success: true };
      }

      return { success: false, nextStep };
    } catch (authError) {
      setLocalError(authError.message);
      return { success: false, error: authError.message };
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

  const resendConfirmationCode = async (username) => {
    try {
      setLocalError(null);
      await resendSignUpCode({
        username,
      });
      return { success: true };
    } catch (authError) {
      setLocalError(authError.message);
      return { success: false, error: authError.message };
    }
  };

  const logout = async () => {
    try {
      setLocalError(null);
      await signOut();
      dispatch(authLogout());
      return { success: true };
    } catch (authError) {
      setLocalError(authError.message);
      return { success: false, error: authError.message };
    }
  };

  const getAuthToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (authError) {
      console.error('Error getting auth token:', authError);
      return null;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLocalError(null);
      dispatch(setAuthLoading(true));

      // Use signInWithRedirect for OAuth providers
      await signInWithRedirect({ provider: 'Google' });

      // The redirect will handle the authentication flow
      // User will be redirected back to the app after successful authentication
      return { success: true };
    } catch (authError) {
      dispatch(authLoginFailure(authError.message));
      dispatch(setAuthLoading(false));
      return { success: false, error: authError.message };
    }
  };

  const clearInvalidTokens = useCallback(async () => {
    try {
      // Try to get the current session to validate tokens
      await fetchAuthSession();
      // If we get here, tokens are valid
      return true;
    } catch (fetchAuthSessionError) {
      // Tokens are invalid, clear them
      try {
        await signOut();
      } catch (signOutError) {
        // Ignore sign out errors - tokens are already invalid
      }
      dispatch(setAuthUser(null));
      return false;
    }
  }, [dispatch]);

  const value = {
    user,
    loading,
    error: error || localError,
    login,
    signup,
    confirmSignup,
    resendConfirmationCode,
    logout,
    getAuthToken,
    signInWithGoogle,
    checkAuthState,
    fetchUserData,
    clearInvalidTokens,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
